<template>
    <div class="flex flex-col-reverse md:flex-col">
        <div
            :class="[
                'flex flex-col md:flex-row md:items-center mb-4',
                $slots.title ? 'md:justify-between' : 'md:justify-end',
            ]"
        >
            <div v-if="$slots.title" class="hidden md:block">
                <slot name="title" />
            </div>

            <div
                class="flex pb-3 mt-5 md:pb-0 md:mt-0 justify-center md:justify-end gap-x-4"
            >
                <ArrowButton
                    v-if="nbrPages > 1 && !loading"
                    v-for="i in 2"
                    @click="swapPage(i == 2)"
                    :direction="i == 2 ? 'right' : 'left'"
                    :colorClass="
                        modelClass != null
                            ? `bg-${modelClass} text-${modelClass}-text hover:bg-${modelClass}-hover`
                            : 'bg-primary text-primary-text hover:bg-primary-hover'
                    "
                    :widthClass="smallButton ? 'w-8' : 'w-16'"
                    :heightClass="smallButton ? 'h-8' : 'h-16'"
                    :paddingClass="smallButton ? 'p-0.5' : 'p-0'"
                />
            </div>
        </div>
        <ReactiveHeightTransition>
            <CarouselTransition
                :slidingLeft="slidingLeft"
                @canTransition="(can) => (canTransition = can)"
                class=""
            >
                <div
                    :key="itemPages[currentPage]?.index"
                    :class="[xGap, gridClass, 'grid grid-cols-1']"
                >
                    <GridCard
                        v-for="item in itemPages[currentPage]?.items"
                        :loading="loading"
                        :key="item.id"
                        :item="item"
                        :divider="false"
                        :aspectRatio="imageAspectRatio"
                        :xGapSize="xGap"
                        :dividerType="useDivider ? 'full' : null"
                        :showDescription="showDescription"
                        :smallTitle="smallTitle"
                        :useModelBackgroundOnFeatured="true"
                    />
                </div>
            </CarouselTransition>
        </ReactiveHeightTransition>

        <div v-if="$slots.title" class="md:hidden">
            <slot name="title" />
        </div>
    </div>
</template>

<script>
import GridCard from '@/Components/DataTable/Grid/GridCard.vue'
import ArrowButton from '@/Components/Elements/ArrowButton.vue'
import { mapState } from 'pinia'
import { screenStore } from '@/Store/screen'

import CarouselTransition from '@/Components/Transitions/CarouselTransition.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
export default {
    name: 'CarouselGridTemplate',

    emits: ['toggleVisibility'],

    components: {
        GridCard,
        ArrowButton,
        CarouselTransition,
        ReactiveHeightTransition,
    },

    props: {
        items: {
            type: Array,
            required: true,
        },
        modelClass: {
            type: String,
            default: null,
        },
        smallButton: {
            type: Boolean,
            default: false,
        },
        imageAspectRatio: {
            type: String,
            required: false,
        },
        useDivider: {
            type: Boolean,
            default: false,
        },
        // Decides if we can show a page that does not have the full nbrItems
        allowIncompletePage: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        maxNbrItems: {
            type: Number,
            default: 4,
            validator: (value) => [3, 4, 5].includes(value),
        },
        showDescription: {
            type: Boolean,
            default: true,
        },
        smallTitle: {
            type: Boolean,
            default: false,
        },
        useItemUrl: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        currentPage: 0,
        slidingLeft: false,
        canTransition: true,
    }),

    mounted() {
        this.$emit('toggleVisibility', this.nbrPages > 0)
    },

    computed: {
        ...mapState(screenStore, ['currentBreakpoint']),

        xGap() {
            if (this.currentBreakpoint == 'xl') {
                return 'gap-x-[16px]'
            }
            return 'gap-x-[7px]'
        },

        nbrItems() {
            switch (this.currentBreakpoint) {
                case null:
                case 'sm':
                    return 1
                case 'md':
                    return this.maxNbrItems - 2
                case 'lg':
                    return this.maxNbrItems - 1
                case 'xl':
                default:
                    return this.maxNbrItems
            }
        },

        gridClass() {
            if (this.maxNbrItems == 5) {
                return 'md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
            } else if (this.maxNbrItems == 4) {
                return 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            } else if (this.maxNbrItems == 3) {
                return 'lg:grid-cols-2 xl:grid-cols-3'
            }
        },

        /**
         * Returns the number of pages
         * If allowIncompletePage is true, and there are less items than nbrItems,
         * return 1 page, otherwise return the number of pages
         */
        nbrPages() {
            let nbrPages = Math.floor(this.items.length / this.nbrItems)
            if (
                nbrPages == 0 &&
                this.items.length > 0 &&
                this.allowIncompletePage
            ) {
                return 1
            }
            return nbrPages
        },

        /*
         * Divide the formattedItems up by nbrItems
         *
         * If the data is still loading, show a single page with loading items
         */
        itemPages() {
            const pages = []

            if (this.loading) {
                const items = Array.from(Array(this.nbrItems).keys())
                pages.push({
                    index: 0,
                    items: items.map((i) => ({
                        id: i,
                    })),
                })
            } else {
                for (let i = 0; i < this.nbrPages; i++) {
                    pages.push({
                        index: i,
                        items: this.formattedItems.slice(
                            this.nbrItems * i,
                            this.nbrItems * (i + 1)
                        ),
                    })
                }
            }

            return pages
        },

        /**
         * Format the items to be compatible with the grid card component
         * and slice the items to show only the current page
         */
        formattedItems() {
            let mapItem
            return this.items.map((item) => {
                const url = this.useItemUrl
                        ? item.url
                        : this.$localizedRoute(
                              `${item.modelClass}.show`,
                              item.id
                          ),
                    mapItem = {
                        id: item.id,
                        name: item.name,
                        modelClass: item.modelClass,
                        url: url,
                        description: item.description,
                        imageUrl: item.imageUrl,
                        coverImageUrl: item.coverImageUrl,
                        date: item.date ?? item.created_at,
                        type: item.type
                            ? item.type.shortname
                                ? item.type.shortname
                                : item.type.name
                            : null,
                        attributes: item.attributes,
                        can: item.can,
                        is_draft: item.is_draft ?? false,
                        is_featured: item.is_featured ?? false,
                    }
                if (item.organization) {
                    mapItem.subtitleLink = {
                        url: this.$localizedRoute(
                            'organization.show',
                            item.organization.id
                        ),
                        text: item.organization.name,
                    }
                }

                return mapItem
            })
        },
    },

    watch: {
        /**
         * If the page nbr does not exist anymore after changing screensize
         * reset the current page to 0
         */
        currentBreakpoint() {
            if (this.formattedItems.length < this.nbrItems) {
                this.currentPage = 0
            }
        },

        nbrPages() {
            this.$emit('toggleVisibility', this.nbrPages > 0)
        },
    },

    methods: {
        swapPage(nextPage) {
            if (this.canTransition) {
                if (nextPage) {
                    this.slidingLeft = false
                    this.currentPage =
                        this.currentPage == this.nbrPages - 1
                            ? 0
                            : this.currentPage + 1
                } else {
                    this.slidingLeft = true
                    this.currentPage =
                        this.currentPage == 0
                            ? this.nbrPages - 1
                            : this.currentPage - 1
                }
            }
        },
    },
}
</script>
